import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { TARIFIERUNGSARTEN } from "./data";
import type { TarifierungKey, VertragsartKey } from "./types";

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 600px)": {
      gridTemplateColumns: "1fr 1fr",
    },
    "@media (min-width: 1024px)": {
      gridTemplateColumns: "repeat(4, 1fr)",
    },
  },
  tile: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.cardBg,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    rowGap: "10px",
    paddingTop: "20px",
    paddingBottom: "18px",
    paddingLeft: "12px",
    paddingRight: "12px",
    minHeight: "168px",
    textAlign: "center",
    ...shorthands.border("2px", "solid", BRAND.border),
    ...shorthands.borderRadius("12px"),
    transitionProperty: "border-color, box-shadow, transform, background-color",
    transitionDuration: "140ms",
    ":hover": {
      ...shorthands.borderColor("rgba(230,106,44,0.6)"),
      transform: "translateY(-2px)",
    },
  },
  tileActive: {
    ...shorthands.borderColor(BRAND.pillarOrange),
    backgroundColor: "rgba(230,106,44,0.06)",
    boxShadow: "0 0 0 4px rgba(230,106,44,0.18)",
  },
  iconWrap: {
    color: BRAND.pillarOrange,
    width: "72px",
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: "1rem",
    fontWeight: 700,
    color: BRAND.ink,
    lineHeight: 1.2,
  },
  desc: {
    fontSize: "0.78rem",
    color: BRAND.muted,
    lineHeight: 1.3,
  },
});

const Svg = ({ children }: { children: React.ReactNode }) => (
  <svg
    width="72"
    height="60"
    viewBox="0 0 72 60"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const Icons: Record<"standard" | "gewerbe" | "stueck" | "sammel", () => JSX.Element> = {
  standard: () => (
    <Svg>
      {/* Single contract sheet with car & checkmark */}
      <rect x="14" y="6" width="44" height="48" rx="3" />
      <path d="M22 18h28M22 26h22M22 34h26" />
      <circle cx="50" cy="44" r="8" fill="currentColor" stroke="none" opacity="0.85" />
      <path d="M46 44l3 3 5-5" stroke="#fff" strokeWidth="2" />
    </Svg>
  ),
  gewerbe: () => (
    <Svg>
      {/* Briefcase + car */}
      <path d="M8 22h56v28H8z" />
      <path d="M28 22v-6h16v6" />
      <path d="M8 32h56" />
      <g transform="translate(20 38)">
        <path d="M2 12l2-6a2 2 0 0 1 1.8-1.4h22a2 2 0 0 1 1.8 1.4l2 6" />
        <circle cx="8" cy="14" r="1.6" fill="currentColor" />
        <circle cx="24" cy="14" r="1.6" fill="currentColor" />
      </g>
    </Svg>
  ),
  stueck: () => (
    <Svg>
      {/* Frame with multiple coins */}
      <rect x="6" y="10" width="60" height="40" rx="3" />
      <path d="M6 18h60" />
      <circle cx="20" cy="34" r="6" />
      <text x="20" y="37" textAnchor="middle" fontFamily="Arial" fontSize="8" fontWeight="700" fill="currentColor" stroke="none">€</text>
      <circle cx="36" cy="34" r="6" />
      <text x="36" y="37" textAnchor="middle" fontFamily="Arial" fontSize="8" fontWeight="700" fill="currentColor" stroke="none">€</text>
      <circle cx="52" cy="34" r="6" />
      <text x="52" y="37" textAnchor="middle" fontFamily="Arial" fontSize="8" fontWeight="700" fill="currentColor" stroke="none">€</text>
    </Svg>
  ),
  sammel: () => (
    <Svg>
      {/* Stack of contracts under one umbrella */}
      <path d="M36 6c-14 0-24 8-24 18h48c0-10-10-18-24-18z" />
      <path d="M36 24v22" />
      <path d="M36 46c0 4 6 4 6 0" />
      <rect x="14" y="32" width="44" height="22" rx="2" />
      <path d="M22 38h28M22 44h22" />
    </Svg>
  ),
};

type Props = {
  value: TarifierungKey | "";
  onChange: (value: TarifierungKey) => void;
  vertragsart?: VertragsartKey | "";
};

const TarifierungPicker = ({ value, onChange, vertragsart }: Props) => {
  const styles = useStyles();
  const items = vertragsart
    ? TARIFIERUNGSARTEN.filter((t) => t.availableFor.includes(vertragsart))
    : TARIFIERUNGSARTEN;
  return (
    <div className={styles.grid}>
      {items.map((t) => {
        const Render = Icons[t.iconKey];
        const active = value === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={mergeClasses(styles.tile, active && styles.tileActive)}
            aria-pressed={active}
          >
            <span className={styles.iconWrap}>
              <Render />
            </span>
            <span className={styles.title}>{t.label}</span>
            <span className={styles.desc}>{t.description}</span>
          </button>
        );
      })}
    </div>
  );
};

export default TarifierungPicker;
